import React, { useEffect, useMemo, useState } from "react";
import {
    adminListHomeBanners,
    adminCreateHomeBanner,
    adminUpdateHomeBanner,
    adminDeleteHomeBanner,
} from "../services/homeBannersService";
import { uploadToStorage, deleteFromStorage } from "../services/storageService";

const emptyForm = {
    active: true,
    order: 1,
    placement: "mid",
    type: "image",
    src: "",
    storagePath: "",
    poster: "",
    posterPath: "",
    badge: "",
    title: "",
    subtitle: "",
    ctaText: "Voir",
    ctaTo: "/catalogue",
    fit: "cover",
    objectPosition: "center",
    interval: 4500,
};

export default function AdminHomeBanners() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [saving, setSaving] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const [fileMain, setFileMain] = useState(null);
    const [filePoster, setFilePoster] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const list = await adminListHomeBanners();
            setRows(Array.isArray(list) ? list : []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const resetForm = () => {
        setEditingId(null);
        setForm(emptyForm);
        setFileMain(null);
        setFilePoster(null);
    };

    const startEdit = (b) => {
        setEditingId(b.id);
        setForm({
            ...emptyForm,
            ...b,
            order: Number(b.order || 1),
            interval: Number(b.interval || 4500),
            active: !!b.active,
        });
        setFileMain(null);
        setFilePoster(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            let payload = { ...form };

            // upload media principal si changé
            if (fileMain) {
                // supprime ancien
                await deleteFromStorage(payload.storagePath);
                const ext = (fileMain.name.split(".").pop() || "bin").toLowerCase();
                const path = `homeBanners/${Date.now()}_${Math.random().toString(16).slice(2)}.${ext}`;
                const up = await uploadToStorage(fileMain, path);
                payload.src = up.url;
                payload.storagePath = up.path;

                // si video => type=video
                if (fileMain.type.startsWith("video/")) payload.type = "video";
                if (fileMain.type.startsWith("image/")) payload.type = "image";
            }

            // poster (optionnel)
            if (filePoster) {
                await deleteFromStorage(payload.posterPath);
                const ext = (filePoster.name.split(".").pop() || "jpg").toLowerCase();
                const path = `homeBanners/posters/${Date.now()}_${Math.random().toString(16).slice(2)}.${ext}`;
                const up = await uploadToStorage(filePoster, path);
                payload.poster = up.url;
                payload.posterPath = up.path;
            }

            payload.order = Number(payload.order || 1);
            payload.interval = Number(payload.interval || 4500);

            if (!payload.src) {
                alert("Ajoute une image ou une vidéo (champ media).");
                setSaving(false);
                return;
            }

            if (editingId) {
                await adminUpdateHomeBanner(editingId, payload);
            } else {
                await adminCreateHomeBanner(payload);
            }

            await load();
            resetForm();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de l’enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (b) => {
        if (!window.confirm("Supprimer cette bannière ?")) return;
        try {
            await deleteFromStorage(b.storagePath);
            await deleteFromStorage(b.posterPath);
            await adminDeleteHomeBanner(b.id);
            await load();
            if (editingId === b.id) resetForm();
        } catch (e) {
            console.error(e);
            alert("Suppression impossible.");
        }
    };

    const toggleActive = async (b) => {
        await adminUpdateHomeBanner(b.id, { active: !b.active });
        await load();
    };

    const sorted = useMemo(() => {
        return [...rows].sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    }, [rows]);

    return (
        <div className="container py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h5 m-0">Admin • Bannières Slider</h1>
                <button className="btn btn-outline-secondary btn-sm" onClick={resetForm}>
                    Nouveau
                </button>
            </div>

            {/* Form */}
            <form className="card shadow-sm mb-4" onSubmit={onSubmit}>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-12 col-md-3">
                            <label className="form-label">Actif</label>
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={!!form.active}
                                    onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                                    id="active"
                                />
                                <label className="form-check-label" htmlFor="active">
                                    Afficher sur le site
                                </label>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <label className="form-label">Placement</label>
                            <select
                                className="form-select"
                                value={form.placement || "mid"}
                                onChange={(e) => setForm((p) => ({ ...p, placement: e.target.value }))}
                            >
                                <option value="top">Top (après Hero)</option>
                                <option value="after_categories">Après catégories</option>
                                <option value="mid">Milieu</option>
                                <option value="after_featured">Après Featured</option>
                                <option value="bottom">Bottom (fin de page)</option>
                            </select>
                        </div>

                        <div className="col-6 col-md-3">
                            <label className="form-label">Ordre</label>
                            <input
                                type="number"
                                className="form-control"
                                value={form.order}
                                onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
                            />
                        </div>

                        <div className="col-6 col-md-3">
                            <label className="form-label">Type</label>
                            <select
                                className="form-select"
                                value={form.type}
                                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
                            >
                                <option value="image">Image</option>
                                <option value="video">Vidéo</option>
                            </select>
                        </div>

                        <div className="col-12 col-md-3">
                            <label className="form-label">Interval (image)</label>
                            <input
                                type="number"
                                className="form-control"
                                value={form.interval}
                                onChange={(e) => setForm((p) => ({ ...p, interval: e.target.value }))}
                            />
                            <div className="form-text">En ms. Ignoré si vidéo.</div>
                        </div>

                        <div className="col-12">
                            <label className="form-label">Media (upload image/vidéo)</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/*,video/*"
                                onChange={(e) => setFileMain(e.target.files?.[0] || null)}
                            />
                            {form.src && (
                                <div className="form-text">
                                    Actuel: <a href={form.src} target="_blank" rel="noreferrer">ouvrir</a>
                                </div>
                            )}
                        </div>

                        {form.type === "video" && (
                            <div className="col-12">
                                <label className="form-label">Poster (optionnel, image)</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*"
                                    onChange={(e) => setFilePoster(e.target.files?.[0] || null)}
                                />
                                {form.poster && (
                                    <div className="form-text">
                                        Poster actuel: <a href={form.poster} target="_blank" rel="noreferrer">ouvrir</a>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="col-12 col-md-4">
                            <label className="form-label">Badge</label>
                            <input
                                className="form-control"
                                value={form.badge}
                                onChange={(e) => setForm((p) => ({ ...p, badge: e.target.value }))}
                                placeholder="Nouveau drop / -20% ..."
                            />
                        </div>


                        <div className="col-12 col-md-8">
                            <label className="form-label">Titre</label>
                            <input
                                className="form-control"
                                value={form.title}
                                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                                placeholder="Collection Weekend"
                            />
                        </div>

                        <div className="col-12">
                            <label className="form-label">Sous-titre</label>
                            <input
                                className="form-control"
                                value={form.subtitle}
                                onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                                placeholder="Looks simples, chic et confort..."
                            />
                        </div>

                        <div className="col-12 col-md-4">
                            <label className="form-label">CTA texte</label>
                            <input
                                className="form-control"
                                value={form.ctaText}
                                onChange={(e) => setForm((p) => ({ ...p, ctaText: e.target.value }))}
                            />
                        </div>

                        <div className="col-12 col-md-8">
                            <label className="form-label">CTA lien (route)</label>
                            <input
                                className="form-control"
                                value={form.ctaTo}
                                onChange={(e) => setForm((p) => ({ ...p, ctaTo: e.target.value }))}
                                placeholder="/catalogue?search=robe"
                            />
                        </div>

                        <div className="col-12 col-md-4">
                            <label className="form-label">Fit</label>
                            <select
                                className="form-select"
                                value={form.fit}
                                onChange={(e) => setForm((p) => ({ ...p, fit: e.target.value }))}
                            >
                                <option value="cover">cover</option>
                                <option value="contain">contain</option>
                            </select>
                        </div>

                        <div className="col-12 col-md-8">
                            <label className="form-label">Object position</label>
                            <input
                                className="form-control"
                                value={form.objectPosition}
                                onChange={(e) => setForm((p) => ({ ...p, objectPosition: e.target.value }))}
                                placeholder="center / center top / 50% 30% ..."
                            />
                        </div>
                    </div>
                </div>

                <div className="card-footer d-flex gap-2 justify-content-end">
                    <button type="button" className="btn btn-outline-secondary" onClick={resetForm} disabled={saving}>
                        Annuler
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                        {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Créer"}
                    </button>
                </div>
            </form>

            {/* List */}
            <div className="card shadow-sm">
                <div className="card-body">
                    {loading ? (
                        <div className="text-muted">Chargement...</div>
                    ) : sorted.length === 0 ? (
                        <div className="text-muted">Aucune bannière.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr>
                                        <th>Ordre</th>
                                        <th>Aperçu</th>
                                        <th>Titre</th>
                                        <th>Type</th>
                                        <th>Actif</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((b) => (
                                        <tr key={b.id}>
                                            <td style={{ width: 80 }}>{b.order}</td>
                                            <td style={{ width: 110 }}>
                                                {b.type === "video" ? (
                                                    <div className="ratio ratio-16x9 bg-dark rounded overflow-hidden">
                                                        {b.poster ? (
                                                            <img src={b.poster} alt="" className="w-100 h-100" style={{ objectFit: "cover" }} />
                                                        ) : (
                                                            <div className="d-grid place-items-center text-white small p-2">VIDEO</div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="ratio ratio-16x9 bg-light rounded overflow-hidden">
                                                        <img src={b.src} alt="" className="w-100 h-100" style={{ objectFit: "cover" }} />
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <div className="fw-semibold">{b.title || "-"}</div>
                                                <div className="text-muted small text-truncate" style={{ maxWidth: 420 }}>
                                                    {b.subtitle || ""}
                                                </div>
                                                <div className="small">
                                                    <span className="text-muted">CTA:</span> {b.ctaTo || "-"}
                                                </div>
                                            </td>
                                            <td style={{ width: 90 }}>
                                                <span className="badge text-bg-secondary">{b.type}</span>
                                            </td>
                                            <td style={{ width: 90 }}>
                                                <button
                                                    className={`btn btn-sm ${b.active ? "btn-success" : "btn-outline-secondary"}`}
                                                    onClick={() => toggleActive(b)}
                                                >
                                                    {b.active ? "ON" : "OFF"}
                                                </button>
                                            </td>
                                            <td className="text-end" style={{ width: 220 }}>
                                                <div className="btn-group">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => startEdit(b)}>
                                                        Modifier
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => remove(b)}>
                                                        Supprimer
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
